// ===========================================================================
// ReHome — Tulajdonság-szótár (trait vocabulary)
//
// A platform „Tulajdonságok" szűrője csak akkor működik jól, ha ugyanazt a
// jelentést mindig ugyanaz a kulcsszó képviseli. A menhelyek viszont szabad
// szöveget írnak: "kedves", "barátságos", "szeretetteljes" — külön chip
// mindegyikből, és egyik szűrés sem hoz teljes listát.
//
// Feloldás három lépcsőben, a drágától a legolcsóbb felé haladva:
//   1. DB cache  — resolve_traits() RPC: a már egyszer eldöntött szavak
//                  (trait_canonicals + trait_aliases, 033_trait_vocabulary.sql)
//   2. Gemini    — csak a cache-miss szavakra, a teljes szótárat átadva
//   3. visszaírás— register_trait_mapping() RPC, hogy legközelebb már az
//                  1. lépcső válaszoljon (egy szó jelentését pontosan egyszer
//                  kell LLM-mel eldönteni)
//
// A menhely szövege (bio, megfigyelések) SOSEM változik — kizárólag az, hogy
// melyik kulcsszó kerül az állat `traits` tömbjébe.
// ===========================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from './supabase';
import { normalizeTraitCandidates, isGeminiConfigured } from './geminiService';
import { toast } from './ui-utils';

const isDev = () => {
  try { return (import.meta as any).env?.DEV; } catch { return false; }
};

/** Hány kulcsszót adunk át a Gemininek / ajánlunk fel a staffnak. */
const VOCABULARY_LIMIT = 300;

/**
 * Az SQL normalize_trait() (033_trait_vocabulary.sql) betű szerinti párja:
 * ugyanaz a translate() karakterkészlet, ugyanaz a whitespace/írásjel vágás.
 * A két oldalnak EGYEZNIE kell — ezen a kulcson dől el, mi számít ugyanannak
 * a szónak, és a kliens ezzel keresi ki a DB-től kapott `normalized` értékeket.
 * Szándékosan nem NFD-alapú ékezetlevágás: az minden nyelv ékezetét levágná
 * (pl. a cseh „č"-t), az SQL viszont csak a magyar készletet — a kettő eltérő
 * kulcsot adna, és a leképezés csendben mellényúlna.
 */
const TRAIT_ACCENTS: Record<string, string> = {
  á: 'a', é: 'e', í: 'i', ó: 'o', ö: 'o', ő: 'o', ú: 'u', ü: 'u', ű: 'u',
  Á: 'A', É: 'E', Í: 'I', Ó: 'O', Ö: 'O', Ő: 'O', Ú: 'U', Ü: 'U', Ű: 'U',
};

export const normalizeTrait = (label: string): string =>
  (label || '')
    .replace(/[áéíóöőúüűÁÉÍÓÖŐÚÜŰ]/g, c => TRAIT_ACCENTS[c])
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    // A záró írásjel és a whitespace tetszőleges sorrendben állhat ("kedves ."),
    // ezért egyetlen mintában vágjuk — ugyanígy az SQL normalize_trait()-ben.
    .replace(/[.,;:!?\s]+$/, '');

export type TraitResolutionSource =
  /** A szó (vagy szinonimája) már a szótárban volt — nem kellett AI-hívás. */
  | 'cache'
  /** A Gemini egy meglévő kulcsszóra képezte le. */
  | 'ai-match'
  /** A Gemini szerint új fogalom — bekerült a szótárba. */
  | 'ai-new'
  /** Nem sikerült feloldani (nincs AI / nincs DB) — a szó változatlanul marad. */
  | 'unresolved';

export interface TraitResolution {
  /** A menhely által beírt / az AI által javasolt eredeti kifejezés. */
  input: string;
  /** Az állathoz ténylegesen elmentendő kulcsszó. */
  canonical: string;
  source: TraitResolutionSource;
}

/** Sorrendtartó dedup a normalizált kulcs alapján. */
const dedupe = (labels: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of labels) {
    const label = (raw || '').trim();
    const key = normalizeTrait(label);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
};

/**
 * A szótár aktuális kulcsszavai, népszerűség szerint csökkenő sorrendben.
 * Publikusan olvasható (a szűrő chipjeit és az admin javaslatait is ez adja).
 */
export const fetchTraitVocabulary = async (limit = VOCABULARY_LIMIT): Promise<string[]> => {
  if (!isSupabaseConfigured()) return [];
  try {
    const { data, error } = await supabase
      .from('trait_canonicals')
      .select('label')
      .order('usage_count', { ascending: false })
      .order('label', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return (data || []).map((r: { label: string }) => r.label);
  } catch (err: any) {
    if (isDev()) console.error('[fetchTraitVocabulary]', err);
    return [];
  }
};

/**
 * Feloldja a megadott kifejezéseket kanonikus kulcsszavakra.
 * Sosem dob és sosem veszít el kulcsszót: ha a DB vagy a Gemini nem elérhető,
 * a bemenet változatlanul jön vissza `unresolved` forrással (az állat így is
 * megkapja a tulajdonságot, csak a szótár nem bővül — legközelebb, működő
 * AI mellett a szó rendes leképezést kap).
 */
export const resolveTraits = async (labels: string[]): Promise<TraitResolution[]> => {
  const inputs = dedupe(labels);
  if (inputs.length === 0) return [];

  const asIs = (source: TraitResolutionSource = 'unresolved'): TraitResolution[] =>
    inputs.map(input => ({ input, canonical: input, source }));

  if (!isSupabaseConfigured()) return asIs();

  // ─── 1. DB cache ─────────────────────────────────────────────────────────
  const resolved = new Map<string, TraitResolution>();
  try {
    const { data, error } = await supabase.rpc('resolve_traits', { p_labels: inputs });
    if (error) throw error;
    for (const row of (data || []) as { input_label: string; canonical_label: string | null }[]) {
      if (!row?.canonical_label) continue;
      resolved.set(normalizeTrait(row.input_label), {
        input: row.input_label,
        canonical: row.canonical_label,
        source: 'cache',
      });
    }
  } catch (err: any) {
    if (isDev()) console.error('[resolveTraits] cache lookup failed', err);
    return asIs();
  }

  const misses = inputs.filter(i => !resolved.has(normalizeTrait(i)));

  // ─── 2. Gemini (csak a cache-missekre) ───────────────────────────────────
  if (misses.length > 0 && isGeminiConfigured()) {
    const vocabulary = await fetchTraitVocabulary();
    const suggestions = await normalizeTraitCandidates(misses, vocabulary);

    // A modell isNew jelzésére nem hagyatkozunk: a szótárat MI küldtük, tehát
    // itt is el tudjuk dönteni, hogy a javasolt címke létező kulcsszó-e.
    // A gemini-proxy ugyanezt elvégzi, de a két deploy elcsúszhat (régi edge
    // function + új frontend), és egy kitalált címkére hivatkozó válasz
    // ilyenkor is hibát dobna a DB-ben ahelyett, hogy új fogalom lenne.
    const vocabByNorm = new Map(vocabulary.map(v => [normalizeTrait(v), v]));
    const byInput = new Map(
      suggestions
        .filter(s => s && typeof s.input === 'string' && typeof s.canonical === 'string' && s.canonical.trim())
        .map(s => {
          const known = vocabByNorm.get(normalizeTrait(s.canonical));
          return [
            normalizeTrait(s.input),
            known ? { ...s, canonical: known, isNew: false } : { ...s, canonical: s.canonical.trim(), isNew: true },
          ] as const;
        })
    );

    // ─── 3. Visszaírás a cache-be ──────────────────────────────────────────
    for (const miss of misses) {
      const suggestion = byInput.get(normalizeTrait(miss));
      // AI nélkül nem írunk a szótárba: egy ismeretlen szót kanonikusként
      // rögzíteni azt jelentené, hogy a jelentését soha többé nem vizsgáljuk
      // felül — pont a duplikátumokat termelnénk újra.
      if (!suggestion) continue;

      try {
        // Új fogalomnál is a Gemini tömör alakját kérjük kulcsszónak
        // ("nagyon szeret játszani" -> chip: "Játékos", a beírt szöveg pedig
        // aliasként megmarad). A p_create_canonical csak ilyenkor igaz: ha a
        // modell MEGLÉVŐ szótári szóra hivatkozik, azt a DB-nek is a saját
        // szótárában kell megtalálnia, különben hibát dob.
        const { data, error } = await supabase.rpc('register_trait_mapping', {
          p_label: miss,
          p_canonical_label: suggestion.canonical,
          p_create_canonical: suggestion.isNew,
        });
        if (error) throw error;
        if (typeof data === 'string' && data) {
          resolved.set(normalizeTrait(miss), {
            input: miss,
            canonical: data,
            source: suggestion.isNew ? 'ai-new' : 'ai-match',
          });
        }
      } catch (err: any) {
        // Jogosultság hiánya (nem staff) vagy hálózati hiba — a kulcsszó
        // ilyenkor is használható, csak nem kerül be a szótárba.
        if (isDev()) console.error('[resolveTraits] register failed', miss, err);
      }
    }
  }

  return inputs.map(
    input => resolved.get(normalizeTrait(input)) || { input, canonical: input, source: 'unresolved' as const }
  );
};

/**
 * A mentés előtti egy lépéses hívás: kanonizált, deduplikált kulcsszólista.
 * Alapból visszajelez a staffnak, ha egy szót másikra cseréltünk — az
 * átnevezés sosem történik némán, és a menhely felül tudja bírálni.
 */
export const canonicalizeTraits = async (
  labels: string[],
  options: { notify?: boolean } = {}
): Promise<string[]> => {
  const { notify = true } = options;
  const resolutions = await resolveTraits(labels);

  if (notify) {
    const remapped = resolutions.filter(
      r => r.source !== 'unresolved' && normalizeTrait(r.input) !== normalizeTrait(r.canonical)
    );
    // Egyetlen összevont üzenet: 5 átnevezett jellemzőnél 5 toast eltakarná a
    // képernyőt, és a staff pont a végeredményt nem látná.
    if (remapped.length > 0) {
      const pairs = remapped.map(r => `„${r.input}" → „${r.canonical}"`).join(', ');
      toast.info(`Kulcsszavak egységesítve: ${pairs}. Így a keresésben egy helyre kerülnek — a leírás szövege nem változott.`);
    }
  }

  return dedupe(resolutions.map(r => r.canonical));
};

/**
 * Megjelenítési idejű kanonizáló: normalizált szó -> kanonikus címke.
 * A szótár (trait_canonicals) és a cache (trait_aliases) egyben, publikus
 * olvasással.
 */
export const fetchTraitCanonicalMap = async (): Promise<Map<string, string>> => {
  const map = new Map<string, string>();
  if (!isSupabaseConfigured()) return map;

  try {
    // Két külön lekérés + kliensoldali join, szándékosan PostgREST-embed
    // helyett: a táblákon oszlopszintű GRANT van (a created_by nem publikus),
    // az embedhez viszont a nem engedélyezett oszlopokhoz is hozzáférés
    // kellene. A két tábla mérete a szótár mérete — pár száz sor.
    const [canonicals, aliases] = await Promise.all([
      supabase.from('trait_canonicals').select('id, normalized, label'),
      supabase.from('trait_aliases').select('alias_normalized, canonical_id'),
    ]);
    if (canonicals.error) throw canonicals.error;
    if (aliases.error) throw aliases.error;

    const labelById = new Map<string, string>();
    for (const row of (canonicals.data || []) as { id: string; normalized: string; label: string }[]) {
      labelById.set(row.id, row.label);
    }

    // Az aliasok mennek előbb: egy kanonikus kulcsszó sosem íródhat felül egy
    // rá mutató aliasszal (a DB-oldali resolve_traits is így rangsorol).
    for (const row of (aliases.data || []) as { alias_normalized: string; canonical_id: string }[]) {
      const label = labelById.get(row.canonical_id);
      if (label) map.set(row.alias_normalized, label);
    }
    for (const row of (canonicals.data || []) as { normalized: string; label: string }[]) {
      map.set(row.normalized, row.label);
    }
  } catch (err: any) {
    if (isDev()) console.error('[fetchTraitCanonicalMap]', err);
  }
  return map;
};

/**
 * Kanonizáló függvény a listázó/szűrő felületekhez. A régebbi, még
 * egységesítés előtt rögzített `animals.traits` értékeket is a helyes chipbe
 * sorolja, anélkül hogy az adatbázist újra kellene írni. Ismeretlen szót
 * változatlanul ad vissza.
 */
export const useTraitCanonicalizer = (): { toCanonical: (label: string) => string; loading: boolean } => {
  const [map, setMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchTraitCanonicalMap().then(m => {
      if (cancelled) return;
      setMap(m);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const toCanonical = useCallback(
    (label: string) => map.get(normalizeTrait(label)) || (label || '').trim(),
    [map]
  );

  return { toCanonical, loading };
};

/**
 * A szótár kulcsszavai — az admin felület javaslataihoz (hogy a staff eleve
 * meglévő kulcsszót válasszon, AI-hívás nélkül).
 */
export const useTraitVocabulary = (): { vocabulary: string[]; loading: boolean; refetch: () => void } => {
  const [vocabulary, setVocabulary] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setVocabulary(await fetchTraitVocabulary());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return { vocabulary, loading, refetch: load };
};
